import { useEffect, useRef, useState } from "react";

/**
 * AnimatedNumber — counts up smoothly to the target value using requestAnimationFrame.
 * - Respects prefers-reduced-motion (snaps directly to the value).
 * - Supports custom formatter for currency / percentage rendering.
 * - Duration scales gently with value magnitude (cap 1200ms).
 */
const AnimatedNumber = ({ value = 0, duration = 900, format = (n) => n.toLocaleString(), className = "" }) => {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(0);

  useEffect(() => {
    const target = Number(value) || 0;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) {
      setDisplay(target);
      return;
    }
    fromRef.current = display;
    startRef.current = null;
    const dur = Math.min(duration, 400 + Math.log10(Math.max(10, Math.abs(target))) * 200);

    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / dur);
      // easeOutExpo
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      setDisplay(fromRef.current + (target - fromRef.current) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return <span className={className}>{format(display)}</span>;
};

export default AnimatedNumber;
